import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mic, User, Mail, Phone, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export default function CompleteProfile() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    mobile_number: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/user/profile');
      const data = response.data;
      setProfile({
        name: data.name || '',
        email: data.email || '',
        mobile_number: data.mobile_number || ''
      });
      
      if (data.name && data.email && data.mobile_number) {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!profile.name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    
    if (!profile.email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    
    if (!profile.mobile_number || profile.mobile_number.length < 10) {
      toast.error('Please enter a valid mobile number');
      return;
    }
    
    setSaving(true);
    try {
      await api.put('/user/profile', {
        name: profile.name.trim(),
        email: profile.email.trim(),
        mobile_number: profile.mobile_number
      });
      
      toast.success('Profile completed successfully!');
      
      if (refreshUser) {
        await refreshUser();
      }
      
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#0b0d10] via-[#0e1216] to-[#0b0d10]">
        <div className="animate-pulse">
          <Mic className="h-12 w-12 text-primary" />
        </div>
      </div>
    );
  }

  const missingFields = [];
  if (!profile.name) missingFields.push('name');
  if (!profile.email) missingFields.push('email');
  if (!profile.mobile_number) missingFields.push('mobile number');

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-b from-[#0b0d10] via-[#0e1216] to-[#0b0d10]">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <User className="h-6 w-6 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold">Complete Your Profile</CardTitle>
          <CardDescription>
            Please provide a few more details to get started with RecordRinse.com
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                Full Name
                {!profile.name && <span className="text-destructive">*</span>}
              </Label>
              <Input
                id="name"
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                placeholder="Enter your full name"
                className={!profile.name ? 'border-primary/50' : ''}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                Email Address
                {!profile.email && <span className="text-destructive">*</span>}
              </Label>
              <Input
                id="email"
                type="email"
                value={profile.email}
                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                placeholder="Enter your email address"
                className={!profile.email ? 'border-primary/50' : ''}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="mobile" className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                Mobile Number
                {!profile.mobile_number && <span className="text-destructive">*</span>}
              </Label>
              <PhoneInput
                country={'us'}
                value={profile.mobile_number}
                onChange={(value) => setProfile({ ...profile, mobile_number: value })}
                inputStyle={{
                  width: '100%',
                  height: '40px',
                  backgroundColor: 'hsl(var(--background))',
                  border: !profile.mobile_number ? '1px solid hsl(var(--primary) / 0.5)' : '1px solid hsl(var(--border))',
                  borderRadius: '0.375rem',
                  color: 'hsl(var(--foreground))',
                }}
                buttonStyle={{
                  backgroundColor: 'hsl(var(--background))',
                  border: !profile.mobile_number ? '1px solid hsl(var(--primary) / 0.5)' : '1px solid hsl(var(--border))',
                  borderRadius: '0.375rem 0 0 0.375rem',
                }}
                dropdownStyle={{
                  backgroundColor: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  color: 'hsl(var(--foreground))',
                }}
                containerClass="phone-input-container"
              />
            </div>

            <div className="pt-4 space-y-3">
              <Button 
                type="submit" 
                className="w-full" 
                size="lg"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Complete Profile'}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              
              <Button 
                type="button"
                variant="ghost" 
                className="w-full text-muted-foreground" 
                onClick={handleSkip}
              >
                Skip for now
              </Button>
            </div>
          </form>
          
          <p className="text-xs text-center text-muted-foreground mt-4">
            You can always update this information later in Settings
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
